import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useLocation, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { GBP, ukDateLong, ymd, priceLabel, DAYS_SHORT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Clock, ChevronLeft, ChevronRight, Check, CalendarDays, MapPin, Phone, User, Star, Scissors, Gift, Layers, Moon, AlertTriangle, ShieldCheck } from "lucide-react";
import { ContactStep, EmailLinkForm, loadRemembered, saveRemembered } from "@/components/ClientAccount";
import { ConsentForm, GuardianForm, consentComplete } from "@/components/ConsentForm";
import { VoucherDialog, CourseDialog, EveningDialog } from "@/components/PublicExtras";
import { SalonHero, ServiceMenu, InfoPanel } from "@/components/SalonHome";

const nextDays = (n) => Array.from({ length: n }, (_, i) => { const d = new Date(); d.setDate(d.getDate() + i); return d; });
const img = (u) => (!u ? null : (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`));

function Stars({ n, className = "h-4 w-4" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${className} ${i <= Math.round(n) ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />)}
    </div>
  );
}

function hoursSummary(biz) {
  const h = biz.opening_hours || {};
  const open = Object.keys(h).filter((d) => !h[d].closed).map(Number).sort();
  if (!open.length) return "";
  const first = h[open[0]];
  const same = open.every((d) => h[d].open === first.open && h[d].close === first.close);
  const contiguous = open.every((d, i) => i === 0 || d === open[i - 1] + 1);
  if (same && contiguous && open.length > 1) return `${DAYS_SHORT[open[0]]}–${DAYS_SHORT[open[open.length - 1]]} ${first.open}–${first.close}`;
  return open.map((d) => `${DAYS_SHORT[d]} ${h[d].open}–${h[d].close}`).join(", ");
}

function PayReturn({ payState, payInfo, slug, accent }) {
  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center animate-fade-up" data-testid="payment-return">
        {payState === "checking" && (<><div className="h-10 w-10 rounded-full border-2 border-stone-300 border-t-stone-800 animate-spin mx-auto" /><p className="mt-4 text-stone-600">Confirming your payment…</p></>)}
        {payState === "paid" && (<>
          <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><Check className="h-8 w-8" /></div>
          {payInfo?.kind === "voucher" ? (<><h2 className="mt-4 font-extrabold text-2xl">Gift voucher sent!</h2>
            {payInfo.voucher && <p className="text-stone-600 text-sm mt-1" data-testid="voucher-code">Voucher <b className="font-mono">{payInfo.voucher.code}</b> for {payInfo.voucher.service_name || GBP(payInfo.voucher.amount)} has been emailed.</p>}</>)
            : payInfo?.kind === "course" ? (<><h2 className="mt-4 font-extrabold text-2xl">Course purchased!</h2>
              {payInfo.course && <p className="text-stone-600 text-sm mt-1">{payInfo.course.sessions_total} × {payInfo.course.service_name}. Book each session online with the same email — it's already paid.</p>}</>)
            : (<><h2 className="mt-4 font-extrabold text-2xl">Deposit paid — you're booked!</h2><p className="text-stone-600 text-sm mt-1">A confirmation has been sent to your email.</p></>)}
          <Link to={`/${slug}`} className="inline-block mt-6 text-sm underline" style={{ color: accent }}>Back to booking</Link>
        </>)}
        {payState === "cancelled" && (<><h2 className="font-extrabold text-2xl">Payment cancelled</h2><p className="text-stone-600 text-sm mt-1">No payment was taken. If you were booking an appointment, it's held and the salon will be in touch.</p><Link to={`/${slug}`} className="inline-block mt-6 text-sm underline" style={{ color: accent }}>Back to booking</Link></>)}
      </div>
    </div>
  );
}

function Header({ biz, reviewData, accent }) {
  const br = biz.branding || {};
  const logo = img(biz.logo_url);
  const marble = br.background === "marble";
  const specials = Object.keys(biz.special_dates || {}).filter((d) => d >= ymd(new Date())).sort().slice(0, 4);
  const cover = img(biz.cover_url);
  return (
    <>
    {cover && <div className="-mx-4 h-44 sm:h-56 overflow-hidden" data-testid="public-cover"><img src={cover} alt={`${biz.name}`} className="h-full w-full object-cover" /></div>}
    <div className={`-mx-4 px-4 ${cover ? "pt-6" : "pt-10"} pb-6 text-center ${marble ? "bg-cover bg-center border-b border-stone-200" : ""}`} style={marble ? { backgroundImage: "linear-gradient(rgba(255,255,255,.55),rgba(255,255,255,.75)), url(/marble.jpg)" } : {}} data-testid="public-header">
      {logo ? (
        <div className="h-16 w-16 rounded-2xl overflow-hidden mx-auto"><img src={logo} alt="" className="h-full w-full object-cover" /></div>
      ) : br.logo_style === "script" ? null : (
        <div className="h-16 w-16 rounded-2xl text-white flex items-center justify-center font-bold text-2xl mx-auto" style={{ background: br.primary || "#1c1917" }}>{biz.name[0]}</div>
      )}
      <h1 className={br.logo_style === "script" && !logo ? "font-script text-5xl leading-tight text-black" : "mt-3 font-extrabold text-2xl"} data-testid="public-business-name">{biz.name}</h1>
      {biz.tagline && <p className="mt-1 text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: accent }}>{biz.tagline}</p>}
      {reviewData.count > 0 && (
        <div className="mt-2 flex items-center justify-center gap-2 text-sm">
          <Stars n={reviewData.average} /><span className="font-semibold">{reviewData.average}</span>
          <span className="text-stone-500">({reviewData.count} review{reviewData.count === 1 ? "" : "s"})</span>
        </div>
      )}
      <div className="mt-2 text-sm text-stone-600 flex items-center justify-center gap-3 flex-wrap">
        {biz.address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{biz.address}</span>}
        {biz.phone && <a href={`tel:${biz.phone}`} className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{biz.phone}</a>}
      </div>
      {hoursSummary(biz) && <div className="mt-1 text-xs text-stone-500 flex items-center justify-center gap-1" data-testid="public-hours"><Clock className="h-3 w-3" />{hoursSummary(biz)}{specials.length > 0 && ` · Also open ${specials.map((d) => ukDateLong(d).replace(/ \d{4}$/, "")).join(", ")}`}</div>}
    </div>
    </>
  );
}

function Gallery({ photos }) {
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  if (!photos.length) return null;
  return (
    <div data-testid="public-gallery">
      <h2 className="font-semibold text-lg mb-2.5">Gallery</h2>
      <div className="flex gap-2 overflow-x-auto pb-2 cal-scroll -mx-4 px-4">
        {photos.map((p, i) => (
          <button key={p} data-testid={`public-gallery-${i}`} onClick={() => setOpen(p)} className="shrink-0 h-28 w-28 sm:h-32 sm:w-32 rounded-xl overflow-hidden"><img src={img(p)} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform" /></button>
        ))}
      </div>
      {open && createPortal(
        <button data-testid="gallery-lightbox" onClick={() => setOpen(null)} className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4">
          <img src={img(open)} alt="" className="max-h-full max-w-full rounded-lg" />
        </button>, document.body
      )}
    </div>
  );
}

function Customise({ service, addons, sel, setSel, accent }) {
  const qs = service.booking_questions || [];
  if (!addons.length && !qs.length) return null;
  const toggle = (id) => setSel((p) => ({ ...p, addons: p.addons.includes(id) ? p.addons.filter((x) => x !== id) : [...p.addons, id] }));
  const compromised = Object.entries(sel.answers).some(([q, a]) => q.toLowerCase().includes("bleached") && a.startsWith("Yes"));
  const nano = Object.values(sel.answers).some((a) => a.startsWith("Nanoplastica"));
  return (
    <div className="mb-6 space-y-4" data-testid="customise-section">
      {qs.map((q, qi) => (
        <div key={q.label}>
          <h3 className="font-semibold text-sm mb-2">{q.label}</h3>
          <div className="flex flex-wrap gap-2">
            {q.options.map((o, oi) => {
              const on = sel.answers[q.label] === o;
              return <button key={o} data-testid={`question-${qi}-option-${oi}`} onClick={() => setSel((p) => ({ ...p, answers: { ...p.answers, [q.label]: o } }))}
                className={`px-3.5 py-2 rounded-full border text-sm ${on ? "text-white border-transparent" : "bg-white border-stone-300"}`} style={on ? { background: accent } : {}}>{o}</button>;
            })}
          </div>
        </div>
      ))}
      {compromised && nano && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex gap-2" data-testid="nanoplastica-warning"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />Nanoplastica isn't suitable for bleached or compromised hair. Please choose Hair Botox or Keratin, or call us to discuss.</p>}
      {addons.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm mb-2">Add extras</h3>
          <div className="space-y-2">
            {addons.map((a) => (
              <label key={a.id} className="flex items-center gap-3 bg-white rounded-xl border border-stone-200 px-3 py-2.5 text-sm cursor-pointer">
                <Checkbox data-testid={`addon-${a.id}`} checked={sel.addons.includes(a.id)} onCheckedChange={() => toggle(a.id)} />
                <span className="flex-1">{a.name}</span><span className="text-xs text-stone-500">+{a.duration_min}m</span><span className="font-mono font-semibold">+{GBP(a.price)}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicBooking() {
  const { slug } = useParams();
  const location = useLocation();
  const isSuccess = location.pathname.endsWith("/payment/success");
  const isCancel = location.pathname.endsWith("/payment/cancel");
  const [payState, setPayState] = useState(isSuccess ? "checking" : isCancel ? "cancelled" : null);
  const [payInfo, setPayInfo] = useState(null);
  const [biz, setBiz] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [slots, setSlots] = useState([]);
  const [bestSlots, setBestSlots] = useState([]);
  const [reviewData, setReviewData] = useState({ reviews: [], average: 0, count: 0 });
  const [step, setStep] = useState(1);
  const [sel, setSel] = useState({ service: null, staff: "any", date: ymd(new Date()), time: null, addons: [], answers: {} });
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", mobile: "", notes: "", postcode: "" });
  const [account, setAccount] = useState(null);
  const [resident, setResident] = useState(false);
  const [consent, setConsent] = useState({});
  const [policyOk, setPolicyOk] = useState(false);
  const [guardian, setGuardian] = useState({});
  const [confirmed, setConfirmed] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [myReview, setMyReview] = useState({ rating: 5, comment: "", sent: false });
  const [dialog, setDialog] = useState(null);
  const [err, setErr] = useState("");
  const [courseActive, setCourseActive] = useState(false);
  const [voucher, setVoucher] = useState({ code: "", ok: null, msg: "" });
  const checkVoucher = async () => {
    try { await api.get(`/public/business/${slug}/voucher-check`, { params: { code: voucher.code, service_id: svc.id } }); setVoucher((v) => ({ ...v, ok: true, msg: "Voucher applied — this treatment is covered" })); setResident(false); }
    catch (e) { setVoucher((v) => ({ ...v, ok: false, msg: formatApiError(e.response?.data?.detail) })); }
  };

  useEffect(() => {
    const mem = loadRemembered();
    if (mem) setContact((c) => ({ ...c, ...mem }));
    api.get("/public/client/me").then(({ data }) => {
      if (!data.signed_in) return;
      setAccount(data.profile);
      const p = data.profile;
      setContact((c) => ({ ...c, email: p.email, first_name: p.first_name || c.first_name, last_name: p.last_name || c.last_name, mobile: p.mobile || c.mobile }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/public/business/${slug}`).then((r) => setBiz(r.data)).catch(() => setNotFound(true));
    api.get(`/public/business/${slug}/services`).then((r) => setServices(r.data)).catch(() => {});
    api.get(`/public/business/${slug}/reviews`).then((r) => setReviewData(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => { window.scrollTo({ top: 0 }); }, [step]);

  useEffect(() => {
    if (!isSuccess) return;
    const sid = new URLSearchParams(location.search).get("session_id");
    if (!sid) { setPayState("paid"); return; }
    let tries = 0, stop = false;
    const poll = async () => {
      tries += 1;
      try { const { data } = await api.get(`/payments/status/${sid}`); setPayInfo(data); if (data.payment_status === "paid") { setPayState("paid"); return; } } catch {}
      if (!stop && tries < 12) setTimeout(poll, 2000); else if (!stop) setPayState("paid");
    };
    poll();
    return () => { stop = true; };
  }, [isSuccess, location.search, slug]);

  const bookable = services.filter((s) => !s.is_addon);
  const addonsFor = (s) => services.filter((a) => a.is_addon && a.addon_for_category === s?.category);
  const selAddons = services.filter((a) => sel.addons.includes(a.id));
  const accent = biz?.branding?.accent || "#B07E4F";
  const promo = (t) => biz?.promotions?.find((p) => p.type === t);

  const pickService = async (s) => {
    setSel((p) => ({ ...p, service: s, staff: "any", time: null, addons: [], answers: {} }));
    setConsent({}); setGuardian({}); setResident(false); setVoucher({ code: "", ok: null, msg: "" });
    const { data } = await api.get(`/public/business/${slug}/staff`, { params: { service_id: s.id } });
    setStaff(data);
    setStep(2);
    window.scrollTo(0, 0);
  };
  const loadSlots = async (date, staffId, addons = sel.addons) => {
    setLoadingSlots(true); setSlots([]); setBestSlots([]);
    try { const { data } = await api.get(`/public/business/${slug}/availability`, { params: { service_id: sel.service.id, staff_id: staffId, date, addon_ids: addons.join(",") } }); setSlots(data.slots); setBestSlots(data.best || []); }
    finally { setLoadingSlots(false); }
  };
  const pickStaff = (id) => { setSel((p) => ({ ...p, staff: id, time: null })); setStep(3); loadSlots(sel.date, id); };
  const pickDate = (d) => { const date = ymd(d); setSel((p) => ({ ...p, date, time: null })); loadSlots(date, sel.staff); };

  const svc = sel.service;
  const base = svc ? svc.price + selAddons.reduce((t, a) => t + a.price, 0) : 0;
  const totalMin = svc ? svc.duration_min + selAddons.reduce((t, a) => t + a.duration_min, 0) : 0;
  const rp = promo("resident");
  const residentEligible = rp && svc && !(rp.exclude_categories || []).includes(svc.category) && !(rp.exclude_weekends && [0, 6].includes(new Date(sel.date + "T00:00:00").getDay()));
  const postcodeOk = contact.postcode.replace(/\s/g, "").toUpperCase().startsWith((rp?.postcode_prefix || "").toUpperCase());
  const discount = resident && residentEligible && postcodeOk ? Math.round(base * rp.percent) / 100 : 0;
  const qsAnswered = (svc?.booking_questions || []).every((q) => sel.answers[q.label]);
  const ds = biz?.deposit_settings;
  const depBase = voucher.ok || courseActive ? base - (svc?.price || 0) : base - discount;
  const dep = !svc || voucher.ok || courseActive || !biz?.online_payments || depBase <= 0 ? 0
    : svc.deposit_override > 0 ? Math.min(svc.deposit_override, depBase)
    : !ds?.enabled ? 0 : Math.round(Math.min(depBase, Math.max(ds.min_amount, depBase * ds.percent / 100)) * 100) / 100;
  const ans = Object.values(sel.answers);
  const nanoBlocked = ans.some((a) => a.startsWith("Nanoplastica")) && ans.some((a) => a.startsWith("Yes - bleached"));
  const goReview = async () => {
    setErr("");
    try { const { data } = await api.get(`/public/business/${slug}/course-check`, { params: { email: contact.email, service_id: svc.id } }); setCourseActive(data.active); } catch { setCourseActive(false); }
    setStep(5);
  };
  const policy = biz?.cancellation_policy || {};
  const detailsOk = contact.first_name && contact.email && contact.mobile && (!svc?.requires_consent || consentComplete(consent)) && (!svc?.requires_guardian || (guardian.name && guardian.signature)) && (!resident || postcodeOk);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/business/${slug}/book`, {
        service_id: svc.id, staff_id: sel.staff, date: sel.date, start: sel.time, ...contact,
        addon_ids: sel.addons, answers: sel.answers, resident_discount: resident && residentEligible,
        consent: svc.requires_consent ? consent : null, guardian: svc.requires_guardian ? guardian : null,
        voucher_code: voucher.ok ? voucher.code : "", accept_policy: policyOk,
      });
      saveRemembered(contact);
      if (data.deposit > 0) {
        const { data: co } = await api.post(`/public/business/${slug}/deposit-checkout`, { appointment_id: data.appointment_id, origin_url: window.location.origin });
        window.location.href = co.checkout_url;
        return;
      }
      setConfirmed(data); setStep(6);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const sendReview = async () => {
    try {
      await api.post(`/public/business/${slug}/review`, { client_name: `${contact.first_name} ${contact.last_name}`.trim() || "Client", rating: myReview.rating, comment: myReview.comment, service_id: svc?.id });
      setMyReview((m) => ({ ...m, sent: true }));
    } catch {}
  };

  if (notFound) return <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[#FAF8F5] text-stone-600" data-testid="public-not-found"><p>This booking page doesn't exist any more.</p><Link to="/" className="underline font-medium text-stone-900">Find another business on Get Booked In</Link></div>;
  if (!biz) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><div className="h-8 w-8 rounded-full border-2 border-stone-300 border-t-stone-800 animate-spin" /></div>;
  if (payState) return <PayReturn payState={payState} payInfo={payInfo} slug={slug} accent={accent} />;

  const featured = bookable.filter((s) => s.featured);
  const categories = [...new Set(bookable.map((s) => s.category || "General"))];
  const cp = promo("course");
  const extras = [
    biz.vouchers_enabled && biz.online_payments && { k: "voucher", icon: Gift, label: "Gift vouchers" },
    cp && biz.online_payments && bookable.some((s) => s.course_eligible) && { k: "course", icon: Layers, label: `Courses · ${cp.percent}% off` },
    biz.evening_requests_enabled && { k: "evening", icon: Moon, label: "Request earlier or evening appointment" },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#FAF8F5]" style={{ "--accent": accent }}>
      {step === 1 && <SalonHero biz={biz} reviewData={reviewData} accent={accent} onBook={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })} />}
      <div className={step === 1 ? "max-w-7xl mx-auto px-4 sm:px-8 pb-24" : "max-w-2xl mx-auto px-4 pb-32"}>
        {step > 1 && <Header biz={biz} reviewData={reviewData} accent={accent} />}

        {step > 1 && step < 6 && (
          <button data-testid="booking-back" onClick={() => setStep(step - 1)} className="text-sm text-stone-600 flex items-center gap-1 mt-4 mb-3"><ChevronLeft className="h-4 w-4" /> Back</button>
        )}

        {step === 1 && (
          <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px] items-start animate-fade-up">
          <div className="space-y-8 min-w-0">
            {extras.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1 cal-scroll -mx-4 px-4">
                {extras.map(({ k, icon: Icon, label }) => (
                  <button key={k} data-testid={`public-extra-${k}`} onClick={() => setDialog(k)} className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full border bg-white text-sm font-medium shadow-sm" style={{ borderColor: accent, color: accent }}><Icon className="h-4 w-4" />{label}</button>
                ))}
              </div>
            )}
            {biz.promotions?.length > 0 && (
              <div className="space-y-2" data-testid="public-promotions">
                {biz.promotions.map((p) => <div key={p.id || p.name} className="rounded-xl px-3.5 py-2.5 text-sm bg-white border" style={{ borderColor: `${accent}55` }}><b style={{ color: accent }}>{p.name}</b> <span className="text-stone-600">— {p.details}</span></div>)}
              </div>
            )}
            {featured.length > 0 && (
              <div>
                <h2 className="font-semibold text-lg mb-2.5 flex items-center gap-1.5"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> Popular treatments</h2>
                <div className="flex gap-3 overflow-x-auto pb-2 cal-scroll -mx-4 px-4">
                  {featured.map((s) => (
                    <button key={s.id} data-testid={`public-featured-${s.id}`} onClick={() => pickService(s)} className="shrink-0 w-48 bg-white rounded-2xl border border-stone-200 overflow-hidden text-left shadow-sm hover:shadow-md transition-shadow">
                      <div className="h-24 overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}22, ${accent}88)` }}>{s.image_url && <img src={img(s.image_url)} alt="" className="h-full w-full object-cover" />}</div>
                      <div className="p-3">
                        <div className="font-semibold text-sm leading-tight line-clamp-2">{s.name}</div>
                        <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-1"><Clock className="h-3 w-3" />{s.duration_min}m</div>
                        <div className="font-mono font-semibold text-sm mt-1">{priceLabel(s)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {bookable.length === 0 && <p className="text-sm text-stone-500">No services available for booking yet.</p>}
            <ServiceMenu services={bookable} accent={accent} onPick={pickService} />
            <Gallery photos={biz.gallery || []} />
            {reviewData.reviews.length > 0 && (
              <div>
                <h2 className="font-extrabold text-2xl tracking-tight mb-3">What clients say</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {reviewData.reviews.slice(0, 4).map((r, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm">
                      <div className="flex items-center gap-2"><Stars n={r.rating} className="h-3.5 w-3.5" /><span className="font-semibold text-sm">{r.client_name}</span></div>
                      {r.comment && <p className="text-sm text-stone-600 mt-1.5">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <InfoPanel biz={biz} accent={accent} onBook={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })} />
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up">
            <h2 className="font-semibold text-xl mb-1">{svc.name}</h2>
            {svc.description && <p className="text-sm text-stone-600 mb-4">{svc.description}</p>}
            {svc.requires_patch_test && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-4 flex gap-2" data-testid="patch-test-notice"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />A patch test is needed at least 24 hours before this treatment. If you haven't had one with us, please call {biz.phone} to arrange it first.</p>}
            <Customise service={svc} addons={addonsFor(svc)} sel={sel} setSel={setSel} accent={accent} />
            <h2 className="font-semibold text-lg mb-3">Choose your specialist</h2>
            {!qsAnswered && <p className="text-xs text-stone-500 mb-2">Please answer the questions above first.</p>}
            <div className={`space-y-2.5 ${qsAnswered && !nanoBlocked ? "" : "opacity-50 pointer-events-none"}`}>
              <button data-testid="public-booking-staff-any" onClick={() => pickStaff("any")} className="w-full bg-white rounded-2xl border border-stone-200 p-4 text-left hover:border-stone-900 transition-colors flex items-center gap-3 shadow-sm">
                <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}><User className="h-5 w-5" /></div>
                <div><div className="font-semibold">Any available</div><div className="text-sm text-stone-500">First free specialist</div></div>
              </button>
              {staff.map((s) => (
                <button key={s.id} data-testid={`public-booking-staff-${s.id}`} onClick={() => pickStaff(s.id)} className="w-full bg-white rounded-2xl border border-stone-200 p-4 text-left hover:border-stone-900 transition-colors flex items-start gap-3 shadow-sm">
                  <div className="h-12 w-12 rounded-full flex items-center justify-center font-semibold text-stone-700 overflow-hidden shrink-0" style={{ background: s.color }}>{s.photo_url ? <img src={img(s.photo_url)} alt="" className="h-full w-full object-cover" /> : s.name.replace(/[^A-Za-z]/g, "")[0]}</div>
                  <div className="min-w-0"><div className="font-semibold">{s.name}</div><div className="text-sm text-stone-500">{s.role_title}</div>{s.bio && <div className="text-xs text-stone-500 mt-1 line-clamp-2">{s.bio}</div>}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-up">
            <h2 className="font-semibold text-lg mb-3">Pick a date & time</h2>
            <div className="flex gap-2 overflow-x-auto pb-2 cal-scroll">
              {nextDays(28).map((d) => {
                const key = ymd(d); const active = key === sel.date;
                return (
                  <button key={key} data-testid={`public-booking-date-${key}`} onClick={() => pickDate(d)}
                    className={`shrink-0 w-16 rounded-xl border p-2 text-center ${active ? "text-white border-transparent" : "bg-white border-stone-200"}`} style={active ? { background: accent } : {}}>
                    <div className="text-[11px] opacity-70">{d.toLocaleDateString("en-GB", { weekday: "short" })}</div>
                    <div className="font-mono font-semibold">{d.getDate()}</div>
                    <div className="text-[10px] opacity-70">{d.toLocaleDateString("en-GB", { month: "short" })}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-4">
              {loadingSlots ? <p className="text-sm text-stone-500">Finding available times…</p> : slots.length === 0 ? (
                <p className="text-sm text-stone-500 bg-white border border-stone-200 rounded-xl p-4">No availability on this day. Try another date.</p>
              ) : (
                <>
                {bestSlots.length > 0 && bestSlots.length < slots.length && (
                  <div className="mb-4" data-testid="best-times">
                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">Suggested times</p>
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                      {bestSlots.map((t) => (
                        <button key={t} data-testid={`public-booking-best-slot-${t}`} onClick={() => { setSel((p) => ({ ...p, time: t })); setStep(4); }}
                          className="py-2.5 rounded-lg border-2 font-mono text-sm font-semibold bg-white transition-colors hover:text-white" style={{ borderColor: accent, color: accent }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = accent; e.currentTarget.style.color = "#fff"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = accent; }}>{t}</button>
                      ))}
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mt-4 mb-2">All times</p>
                  </div>
                )}
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {slots.map((t) => (
                    <button key={t} data-testid={`public-booking-time-slot-button-${t}`} onClick={() => { setSel((p) => ({ ...p, time: t })); setStep(4); }}
                      className="py-2.5 rounded-lg border border-stone-200 bg-white font-mono text-sm hover:border-stone-900 hover:bg-stone-900 hover:text-white transition-colors">{t}</button>
                  ))}
                </div>
                </>
              )}
            </div>
            {biz.evening_requests_enabled && <button data-testid="slots-evening-request" onClick={() => setDialog("evening")} className="mt-4 text-sm underline flex items-center gap-1" style={{ color: accent }}><Moon className="h-3.5 w-3.5" />Need an earlier or evening time? Request one</button>}
          </div>
        )}

        {step === 4 && (
          <div className="animate-fade-up space-y-4">
            <h2 className="font-semibold text-lg">Your details</h2>
            <ContactStep contact={contact} setContact={setContact} account={account} slug={slug} />
            {svc.price_type !== "from" && (
              <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm" data-testid="voucher-redeem-section">
                <Label>Have a treatment gift voucher?</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input data-testid="public-voucher-code" value={voucher.code} onChange={(e) => setVoucher({ code: e.target.value.toUpperCase(), ok: null, msg: "" })} placeholder="GV-XXXXXXXX" className="h-11 rounded-lg font-mono" />
                  <Button data-testid="public-voucher-apply" variant="outline" disabled={!voucher.code.trim()} onClick={checkVoucher} className="h-11 rounded-full">Apply</Button>
                </div>
                {voucher.msg && <p data-testid="public-voucher-msg" className={`text-xs mt-1.5 ${voucher.ok ? "text-emerald-700" : "text-rose-600"}`}>{voucher.msg}</p>}
              </div>
            )}
            {residentEligible && !voucher.ok && (
              <div className="bg-white rounded-2xl border p-4 shadow-sm space-y-3" style={{ borderColor: `${accent}55` }} data-testid="resident-discount">
                <label className="flex items-start gap-2 text-sm"><Checkbox data-testid="resident-checkbox" checked={resident} onCheckedChange={(v) => setResident(!!v)} className="mt-0.5" /><span><b>{rp.name}</b> — I live in a {rp.postcode_prefix} postcode. Can't be combined with other offers.</span></label>
                {resident && <div><Label>Your postcode</Label><Input data-testid="public-postcode" value={contact.postcode} onChange={(e) => setContact((c) => ({ ...c, postcode: e.target.value.toUpperCase() }))} placeholder={`${rp.postcode_prefix} …`} className={`mt-1.5 h-11 rounded-lg ${contact.postcode && !postcodeOk ? "border-rose-400" : ""}`} />{contact.postcode && !postcodeOk && <p className="text-xs text-rose-600 mt-1">Postcode must start with {rp.postcode_prefix}</p>}</div>}
              </div>
            )}
            {svc.requires_guardian && <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm"><GuardianForm value={guardian} onChange={setGuardian} /></div>}
            {svc.requires_consent && <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm"><ConsentForm value={consent} onChange={setConsent} /></div>}
            <Button data-testid="public-review-button" disabled={!detailsOk} onClick={goReview} className="w-full h-12 rounded-full text-white" style={{ background: "#111" }}>Review booking</Button>
          </div>
        )}

        {step === 5 && (
          <div className="animate-fade-up">
            <h2 className="font-semibold text-lg mb-3">Confirm your booking</h2>
            <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm space-y-3">
              {[["Service", svc.name], ...selAddons.map((a) => [`+ ${a.name}`, GBP(a.price)]), ...Object.entries(sel.answers),
                ["Specialist", sel.staff === "any" ? "Any available" : staff.find((s) => s.id === sel.staff)?.name],
                ["Date", ukDateLong(sel.date)], ["Time", sel.time], ["Duration", `${totalMin} min`],
                ["Name", `${contact.first_name} ${contact.last_name}`]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 text-sm"><span className="text-stone-500">{k}</span><span className="font-medium text-right">{v}</span></div>
              ))}
              {voucher.ok && <div className="flex justify-between text-sm text-emerald-700" data-testid="summary-voucher"><span>Gift voucher {voucher.code}</span><span className="font-mono">−{GBP(svc.price)}</span></div>}
              {discount > 0 && !courseActive && !voucher.ok && <div className="flex justify-between text-sm text-emerald-700" data-testid="summary-discount"><span>{rp.name}</span><span className="font-mono">−{GBP(discount)}</span></div>}
              {courseActive && !voucher.ok && <div className="flex justify-between text-sm text-emerald-700" data-testid="summary-course"><span>Prepaid course session</span><span className="font-mono">−{GBP(svc.price)}</span></div>}
              <div className="border-t border-stone-100 pt-3 flex justify-between"><span className="font-semibold">Total</span><span className="font-mono font-bold text-lg" data-testid="summary-total">{voucher.ok || courseActive ? GBP(base - svc.price) : `${svc.price_type === "from" ? "from " : ""}${GBP(base - discount)}`}</span></div>
              {svc.price_type === "from" && <p className="text-xs text-stone-500">Final price is confirmed by your therapist at your appointment.</p>}
              {dep > 0 && <p data-testid="summary-deposit" className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-lg p-2">Pay a {GBP(dep)} deposit now to secure your booking (card, Apple Pay or Google Pay). The rest is paid at the salon.</p>}
            </div>
            {err && <p className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="booking-error">{err}</p>}
            {policy.enabled && (
              <div className="mt-4 bg-white rounded-2xl border border-stone-200 p-4 shadow-sm" data-testid="cancellation-policy">
                <div className="font-semibold text-sm">Cancellation policy</div>
                <p className="text-sm text-stone-600 mt-1 whitespace-pre-line max-h-40 overflow-y-auto" data-testid="cancellation-policy-text">{policy.text}</p>
                {policy.notice_hours > 0 && <p className="text-xs text-stone-500 mt-2">You can cancel online up to {policy.notice_hours} hours before your appointment.</p>}
                <label className="mt-3 flex items-start gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="accept-policy-checkbox" checked={policyOk} onChange={(e) => setPolicyOk(e.target.checked)} className="mt-0.5 h-5 w-5 rounded accent-stone-900" />
                  <span>I have read and accept the cancellation policy</span>
                </label>
              </div>
            )}
            <Button data-testid="public-confirm-button" onClick={submit} disabled={submitting || (policy.enabled && !policyOk)} className="mt-4 w-full h-12 rounded-full text-white font-semibold" style={{ background: accent }}>{submitting ? "Please wait…" : (dep > 0 ? `Pay ${GBP(dep)} deposit & book` : "Confirm booking")}</Button>
          </div>
        )}

        {step === 6 && confirmed && (
          <div className="animate-fade-up text-center pt-6" data-testid="public-booking-confirmation">
            <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><Check className="h-8 w-8" /></div>
            <h2 className="mt-4 font-extrabold text-2xl">You're booked!</h2>
            <p className="text-stone-600 text-sm mt-1">A confirmation has been sent to {contact.email}.</p>
            {!account && <div className="mt-4 bg-white rounded-2xl border border-stone-200 p-4 shadow-sm text-left" data-testid="save-details"><div className="font-semibold text-sm mb-2">Book faster next time</div><EmailLinkForm nextPath="/account" testid="save-details" cta="Save my details" initialEmail={contact.email} /></div>}
            <div className="mt-5 bg-white rounded-2xl border border-stone-200 p-5 shadow-sm text-left space-y-2">
              <div className="flex items-center gap-2 font-semibold"><CalendarDays className="h-4 w-4" style={{ color: accent }} />{confirmed.service_name}</div>
              <div className="text-sm text-stone-600">{confirmed.date_uk} at {confirmed.start} with {confirmed.staff_name}</div>
              <div className="text-sm font-mono font-semibold" data-testid="confirmed-price">{confirmed.promo === "course" ? "Covered by your course" : confirmed.promo === "voucher" ? "Covered by your gift voucher" : `${confirmed.price_type === "from" ? "from " : ""}${GBP(confirmed.price)}`}</div>
            </div>
            <div className="mt-5 bg-white rounded-2xl border border-stone-200 p-5 shadow-sm text-left">
              {myReview.sent ? (
                <p className="text-sm text-emerald-700 flex items-center gap-2"><Check className="h-4 w-4" /> Thanks! Your review has been sent to the salon.</p>
              ) : (
                <>
                  <h3 className="font-semibold">Leave a review</h3>
                  <div className="mt-2 flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button key={i} data-testid={`public-review-star-${i}`} onClick={() => setMyReview((m) => ({ ...m, rating: i }))}>
                        <Star className={`h-7 w-7 ${i <= myReview.rating ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />
                      </button>
                    ))}
                  </div>
                  <Textarea data-testid="public-review-comment" value={myReview.comment} onChange={(e) => setMyReview((m) => ({ ...m, comment: e.target.value }))} className="mt-3 rounded-lg" rows={2} placeholder="Tell others about your experience (optional)" />
                  <Button data-testid="public-review-submit" onClick={sendReview} className="mt-3 w-full rounded-full bg-stone-900 hover:bg-stone-800">Submit review</Button>
                </>
              )}
            </div>
            <p className="mt-6 text-xs text-stone-400">{biz.name} · Powered by Get Booked In</p>
          </div>
        )}
        {step < 6 && <p className="mt-12 text-center text-xs text-stone-400" data-testid="public-powered-by">{biz.name} · Powered by <Link to="/" className="underline">Get Booked In</Link></p>}
      </div>

      {step >= 2 && step <= 3 && svc && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-stone-200 p-4">
          <div className="max-w-xl mx-auto flex items-center justify-between">
            <div><div className="text-xs text-stone-500 truncate max-w-[220px]">{svc.name}</div><div className="font-mono font-bold">{svc.price_type === "from" ? "from " : ""}{GBP(base)} · {totalMin}m</div></div>
            <div className="text-sm text-stone-500">{step === 2 ? "Choose specialist" : sel.time ? sel.time : "Pick a time"}</div>
          </div>
        </div>
      )}

      <VoucherDialog slug={slug} open={dialog === "voucher"} onOpenChange={(v) => setDialog(v ? "voucher" : null)} accent={accent} services={bookable} />
      <CourseDialog slug={slug} open={dialog === "course"} onOpenChange={(v) => setDialog(v ? "course" : null)} accent={accent} services={bookable} promo={cp} />
      <EveningDialog slug={slug} open={dialog === "evening"} onOpenChange={(v) => setDialog(v ? "evening" : null)} accent={accent} services={bookable} />
    </div>
  );
}
